import React, {
  useEffect,
  createContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import GoTrue from 'gotrue-js';
import { nanoid } from "nanoid";
import { navigate } from "gatsby-link";
import { useStaticQuery, graphql } from "gatsby";
import * as Sentry from "@sentry/browser";


export const IdentityContext = createContext();

const IdentityContextProvider = (props) => {
  const [user, setUser] = useState("");
  const [stoData,setStoData] = useState(null); 
  const [caseFormActive,setCaseFormActive] = useState(false);
  const [cases,setCases] = useState(null);
  const [fetchCasesStatus, setFetchCasesStatus] = useState(null);
  const [caseData, setCaseData] = useState(null); 
  const [videoLink,setVideoLink] = useState(null);
  const [fetchUserStatus,setFetchUserStatus] = useState(null);
  const stateOptions = [ 'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FM', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PW', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY' ]; 
  const proxyUrl = "https://stoapi.azurewebsites.net/proxy/";

  const restletId = useMemo(() => {
    return {
      verifyEmail: 1638,
      getOwner: 1639,
      putOwner: 1904,
      getDealer: 1655,
      putDealer: 1906,
      getDealerContact: 1902,
      postDealerContact: 1701,
      putDealerContact: 1702,
      deleteDealerContact: 1703,
      getVan: 1900,
      postVanWarranty: 1901,
      putVanWarranty: 1905,
      getCases: 1645,
      getCase: 1648,
      postCase: 1903,
      putCase: 1801,
      getToken: 1637,
    };
  },[]);

  
  const fetchTokenOptions = useMemo(() => {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        restletid: restletId.getToken,
      }),
    };
  }, [restletId.getToken]);
  
  const auth = useMemo(() => {
    return new GoTrue({
      APIUrl: "https://sto-portal.netlify.app/.netlify/identity",
      audience: "",
      setCookie: true,
    });
  },[]);
  
  useEffect(() => { 
    setUser(auth.currentUser());
    
  },[auth]);
  
  const getUser = useCallback(() => {
    if (user) {
      //get token
      const userType = user.user_metadata.sto_type;
      setFetchUserStatus("processing");  
      if (userType === "Dealer" || userType === "Dealer-Employee") {
        window.dataLayer.push({
          user_type: "Dealer",
        });
      } else if (userType === "Owner") {
        window.dataLayer.push({
          user_type: "Owner",
        });
      }
      fetch(proxyUrl, fetchTokenOptions)
        .then((response) => response.json())
        .then((token) => {
          fetch(proxyUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              restletid:
                userType === "Owner"
                  ? restletId.getOwner
                  : userType === "Dealer"
                  ? restletId.getDealerContact
                  : userType === "Dealer-Employee"
                  ? restletId.getDealerContact
                  : null,
              id: user.user_metadata.sto_id,
              token: token,
            }),
          })
            .then((response) => response.json())
            .then((data) => {
              console.log(data);
              if (data.error) {
                console.log("didnt get record");
                Sentry.captureMessage(
                  "User record not found in Netsuite. Record ID: " +
                    user.user_metadata.sto_id +
                    "User type: " +
                    userType
                );
                Sentry.captureException(data);
                setFetchUserStatus("error");
                setStoData("");
                user
                  .logout()
                  .then((response) => {
                    console.log("User logged out");
                    navigate("/login");
                    setUser("");
                  })
                  .catch((error) => {
                    console.log("Failed to logout user: %o", error);
                  });
              } else {
                setFetchUserStatus("success");
                setStoData(data);
              }
            })
            .catch((error) => {
              console.log("error fetching record.");
              Sentry.captureMessage(
                "Error fetching user record in Netsuite. Record ID: " +
                  user.user_metadata.sto_id +
                  ". User type: " +
                  userType
              );
              Sentry.captureException(error);
              console.log(error);
              setFetchUserStatus("error");
              setStoData("");
              user
                .logout()
                .then((response) => {
                  console.log("User logged out");
                  navigate("/login");
                  setUser("");
                })
                .catch((error) => {
                  console.log("Failed to logout user: %o", error);
                });
            });
        })
        .catch((error) => {
          console.error("Error:", error);
          setStoData("");
          setFetchUserStatus("error");
          Sentry.captureMessage(
            "Error fetching token"
          );
          Sentry.captureException(error);
          user
            .logout()
            .then((response) => {
              console.log("User logged out");
              navigate("/login");
              setUser("");
            })
            .catch((error) => {
              console.log("Failed to logout user: %o", error);
            });
        });
    }
  }, [user, restletId, fetchTokenOptions]);

  useEffect(() => {
    console.log(user);
    user && getUser();
  },[user,getUser])

  //cases 
  const getCases = useCallback(
    (companyId) => {
      setFetchCasesStatus("processing");
      fetch(proxyUrl, fetchTokenOptions)
        .then((response) => response.json())
        .then((token) => {
          fetch(proxyUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              restletid: restletId.getCases,
              token: token,
              id: companyId,
            }),
          })
            .then((response) => response.json())
            .then((caseData) => {
              console.log(caseData);
              setFetchCasesStatus("success");
              if (caseData.status & (caseData.status === "No cases")) {
                setCases([]);
              } else {
                setCases(caseData);
              }
            })
            .catch((error) => {
              setFetchCasesStatus("error");
            });
        })
        .catch((error) => {
          setFetchCasesStatus("error");
        });
    },
    [proxyUrl, restletId, fetchTokenOptions] 
  );
    
  const getCaseData = (caseId) => {
    if (!caseData?.messages) {
      setCaseData({
        fetch_status: "processing"
      });
    }
    fetch(proxyUrl, fetchTokenOptions)
    .then((response) => response.json())
    .then((token) => {
      fetch(
        proxyUrl,
        {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            restletid: restletId.getCase,
            caseid: caseId,
            token: token
          })
        }
      )
        .then((response) => response.json())
        .then((data) => {
          console.log(data);
          if (data.status === "No issue") {
            setCaseData("");
          } else {
            setCaseData({
              ...data,
              fetch_status: "success"
            });
          }
          
          window.dataLayer.push({
            event: "ga4_event",
            eventName: "get_case_info",
          });
        })
        .catch((error) => {
          console.log(error);
          setCaseData({
            fetch_status: "error"
          });
          
        });

    })
    .catch((error) => {
      console.log(error);
      setCaseData({
        fetch_status: "error"
      });
    });
  }

  const generatePassword = () => {
    return nanoid(12);
  }

  const {sanitySiteData} = useStaticQuery(graphql `
    query SupportEmail {
      sanitySiteData {
        supportEmail
      }
    }
  `)
  const {supportEmail} =  sanitySiteData;
  
  return (
    <IdentityContext.Provider
      value={{
        auth,
        user,
        setUser,
        setStoData,
        stoData,
        proxyUrl,
        fetchTokenOptions,
        getUser,
        fetchUserStatus,
        setFetchUserStatus,
        caseFormActive,
        setCaseFormActive,
        getCases,
        cases,
        setCases,
        fetchCasesStatus,
        setFetchCasesStatus,
        getCaseData,
        setCaseData,
        caseData,
        stateOptions,
        generatePassword,
        restletId,
        videoLink,
        setVideoLink,
        supportEmail
      }}
    >
      {props.children}
    </IdentityContext.Provider>
  );
}
export default IdentityContextProvider;